import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setSidebarOverride } from '@/store/sidebar-override.slice'
import { usePageTitleStore } from '@/lib/page-title-store'
import { extractErrorMessage } from '@/lib/error-message'
import { formatPhone } from '@/lib/phone'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ChevronLeftIcon, CopyIcon, EllipsisVerticalIcon, FileUpIcon, FileTextIcon, PencilIcon, PlusIcon } from 'lucide-react'
import {
	Alert, AlertDescription,
	Badge,
	cn,
	ConfirmDialog,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
	DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger,
	DropdownMenuTrigger,
	IconButton,
	TabActionButton,
	Tabs, TabsList, TabsTrigger,
} from '@rentflowdev/ui'
import type { Rental } from '@/types'
import {
	DEFAULT_LOCALE,
	PAYMENT_STATUS_KEYS, PAYMENT_STATUS_VARIANTS,
} from '@/types'
import {
	useCreateDocumentMutation,
	useDeleteRentalMutation,
	useGenerateDocumentUploadUrlMutation,
	useUpdateDocumentMutation,
	useUpdateDocumentStatusMutation,
	useUpdateRentalStatusMutation,
	useGetContactQuery,
	useGetPropertyQuery,
	useGetRentalAutomationQuery,
	useGetRentalQuery,
	useListDocumentTemplatesQuery,
	rentalDocumentsApi,
} from '@/api'
import type { Deal, DocumentTemplate, Property } from '@/types'
import { RENTAL_STATUSES, RENTAL_STATUS_KEYS } from '@/types'
import { AutomationSetupDialog } from '@/components/automation-setup-dialog'
import { CreateDocumentWizard } from '@/components/create-document-wizard'
import { CreateInvoiceDialog } from '@/components/create-invoice-dialog'
import { SelectTemplateDialog } from '@/components/select-template-dialog'
import { ConversationChat } from '@/components/conversation-chat'
import { RentalTermsSection } from '@/components/rental-terms-section'
import { RentalInvoicesSection } from '@/components/rental-invoices-section'
import { RentalDocumentsSection } from '@/components/rental-documents-section'
import { RentalTasksSection } from '@/components/rental-tasks-section'
import { TaskFormDialog } from '@/components/task-form-dialog'
import { RentalNotesSection } from '@/components/rental-notes-section'
import { RentalHistorySection } from '@/components/rental-history-section'
import { RentalAutomationSection } from '@/components/rental-automation-section'
import { DuplicateAutomationDialog } from '@/components/duplicate-automation-dialog'
import { EditRentalDialog } from '@/components/edit-rental-dialog'
import { RentalSummaryBar } from '@/components/rental-summary-bar'
import { allows, type Requirement, useAccess } from '@/lib/access'

export const Route = createFileRoute('/_app/rent/$rentalId')({
	component: RentalDetailPage,
	// The tab lives in the URL so that returning from a document lands on
	// the tab the document was opened from, not on the default one.
	validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
		tab: typeof search.tab === 'string' ? search.tab : undefined,
	}),
})

const RENTAL_TABS = [
	{ value: 'info',          labelKey: 'rent.detail.tabs.info' },
	{ value: 'terms',         labelKey: 'rent.detail.tabs.terms' },
	{ value: 'invoices',      labelKey: 'rent.detail.tabs.invoices' },
	{ value: 'documents',     labelKey: 'rent.detail.tabs.documents' },
	{ value: 'tasks',         labelKey: 'rent.detail.tabs.tasks' },
	{ value: 'notes',         labelKey: 'rent.detail.tabs.notes' },
	{ value: 'automation',    labelKey: 'rent.detail.tabs.automation' },
	{ value: 'history',       labelKey: 'rent.detail.tabs.history' },
] as const

// Tabs that are their own resource in the role catalog: shown only to roles
// that reach it (automation is the owner's and admins').
const TAB_REQUIREMENTS: Partial<Record<(typeof RENTAL_TABS)[number]['value'], Requirement>> = {
	invoices:   { action: 'read', resource: 'Invoice' },
	documents:  { action: 'read', resource: 'Document' },
	tasks:      { action: 'read', resource: 'Task' },
	automation: { action: 'read', resource: 'RentalAutomation' },
}

function formatDate(iso: string) {
	return new Date(iso).toLocaleDateString(DEFAULT_LOCALE, { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Human-readable object type for the contract («2-к квартира», «Дом», ...). */
function formatObjectType(p: Property, t: (key: string) => string): string {
	const baseLabel = t(`properties.types.${p.type}`)
	if (p.type === 'APARTMENT' && p.rooms) {
		return `${p.rooms}-к ${baseLabel.toLowerCase()}`
	}
	return baseLabel
}

function RentalInfoSection({ rental, t }: { rental: Rental; t: (k: string) => string }) {
	const navigate = useNavigate()
	const { data: contact } = useGetContactQuery(rental.tenantContactId)

	return (
		<div className="flex flex-col gap-6">
			<section className="flex flex-col gap-3">
				<h2 className="text-base font-[500] leading-6 text-(--color-foreground)">
					{t('rent.detail.sections.main')}
				</h2>
				<div className="grid grid-cols-[200px_1fr] gap-x-4 gap-y-2 rounded-(--radius-md) border border-(--color-border) p-4">
					<span className="text-sm text-(--color-muted-foreground)">{t('rent.detail.fields.nextPayment')}:</span>
					<span className="text-sm text-(--color-foreground)">
						{rental.nextPaymentDate ? formatDate(rental.nextPaymentDate) : '—'}
					</span>
					<span className="text-sm text-(--color-muted-foreground)">{t('rent.detail.fields.contractNumber')}:</span>
					<span className="text-sm text-(--color-foreground)">№{rental.id.slice(-7)}</span>
					<span className="text-sm text-(--color-muted-foreground)">{t('rent.detail.fields.rentalPeriod')}:</span>
					<span className="text-sm text-(--color-foreground)">
						{formatDate(rental.startDate)} - {rental.endDate ? formatDate(rental.endDate) : '—'}
					</span>
				</div>
			</section>

			<section className="flex flex-col gap-3">
				<h2 className="text-base font-[500] leading-6 text-(--color-foreground)">
					{t('rent.detail.sections.tenant')}
				</h2>
				<div className="grid grid-cols-[200px_1fr] gap-x-4 gap-y-2 rounded-(--radius-md) border border-(--color-border) p-4">
					<span className="text-sm text-(--color-muted-foreground)">{t('rent.detail.fields.fullName')}:</span>
					<span
						className="text-sm text-(--color-info) underline-offset-2 hover:underline cursor-pointer"
						onClick={() => void navigate({ to: '/tenants/$tenantId', params: { tenantId: rental.tenantContactId } })}
					>
						{contact?.name ?? rental.tenantName ?? '—'}
					</span>
					<span className="text-sm text-(--color-muted-foreground)">{t('rent.detail.fields.phone')}:</span>
					<span className="text-sm text-(--color-foreground)">{formatPhone(contact?.phone) || '—'}</span>
					<span className="text-sm text-(--color-muted-foreground)">{t('rent.detail.fields.email')}:</span>
					<span className="text-sm text-(--color-foreground)">{contact?.email ?? '—'}</span>
					<span className="text-sm text-(--color-muted-foreground)">{t('rent.detail.fields.company')}:</span>
					<span className="text-sm text-(--color-muted-foreground)">
						{contact?.company ?? t('rent.detail.fields.notSpecified')}
					</span>
				</div>
			</section>
		</div>
	)
}

function RentalDetailPage() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { rentalId } = Route.useParams()
	const { tab: tabParam } = Route.useSearch()
	const access = useAccess()
	const tabs = RENTAL_TABS.filter(({ value }) => allows(access, TAB_REQUIREMENTS[value]))
	const tab = tabs.some(({ value }) => value === tabParam) ? tabParam! : 'info'
	const canSeeAutomation = allows(access, TAB_REQUIREMENTS.automation)
	// The tabs' own actions, each the gateway's requirement for its call.
	const canCreateTask       = allows(access, { action: 'create', resource: 'Task' })
	const canCreateInvoice    = allows(access, { action: 'create', resource: 'Invoice' })
	const canEditRental       = allows(access, { action: 'update', resource: 'Rental' })
	const canSetUpAutomation  = allows(access, { action: ['create', 'update'], resource: 'RentalAutomation' })
	const canCreateDocument   = allows(access, { action: 'create', resource: 'Document' })
	const canDeleteRental     = allows(access, { action: 'delete', resource: 'Rental' })
	const setTab = (next: string) => {
		void navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, tab: next }), replace: true })
	}
	const [chatCollapsed, setChatCollapsed] = useState(false)
	const [editOpen,   setEditOpen]   = useState(false)
	const [deleteOpen, setDeleteOpen] = useState(false)
	const [automationSetupOpen, setAutomationSetupOpen] = useState(false)
	const [automationMenuOpen, setAutomationMenuOpen] = useState(false)
	const [duplicateOpen, setDuplicateOpen] = useState(false)
	const [duplicateTarget, setDuplicateTarget] = useState<{ rentalId: string; rental: Rental } | null>(null)
	const [docMenuOpen, setDocMenuOpen] = useState(false)
	const [uploadError, setUploadError] = useState<string | null>(null)
	const [selectTemplateOpen, setSelectTemplateOpen] = useState(false)
	const [chosenTemplate, setChosenTemplate] = useState<DocumentTemplate | null>(null)
	const [wizardOpen, setWizardOpen] = useState(false)
	const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false)
	const [taskFormOpen,      setTaskFormOpen]      = useState(false)

	const { data: apiRental, isLoading } = useGetRentalQuery(rentalId)
	const setPageTitle = usePageTitleStore((s) => s.setPageTitle)
	const { data: automation }       = useGetRentalAutomationQuery(rentalId, { skip: !canSeeAutomation })
	const [updateStatus, { isLoading: isStatusUpdating }] = useUpdateRentalStatusMutation()
	const [deleteRental, { isLoading: isDeleting }]       = useDeleteRentalMutation()
	const { data: templatesData }    = useListDocumentTemplatesQuery({ page: 1, limit: 50 })
	const [createDocument]           = useCreateDocumentMutation()
	const [generateUploadUrl]        = useGenerateDocumentUploadUrlMutation()
	const [updateDocument]           = useUpdateDocumentMutation()
	const [updateDocumentStatus]     = useUpdateDocumentStatusMutation()
	const fileInputRef               = useRef<HTMLInputElement>(null)
	const rental    = apiRental ?? undefined
	const templates = templatesData?.templates ?? []
	const dispatch  = useDispatch()

	useEffect(() => {
		if (!rental) return
		const childPath =
			rental.status === 'UPCOMING'  ? '/rent/upcoming'  :
			rental.status === 'CLOSING'   ? '/rent/active'    :
			rental.status === 'COMPLETED' ? '/rent/completed' :
			                                '/rent/active'
		dispatch(setSidebarOverride(childPath))
		return () => { dispatch(setSidebarOverride(null)) }
	}, [rental, dispatch])

	useEffect(() => {
		if (!rental) return
		// propertyTitle already includes the address ("city, address") — don't join both.
		const title = (rental.propertyTitle ?? rental.propertyAddress) || `№${rental.id.slice(-7)}`
		setPageTitle(title)
		return () => setPageTitle(null)
	}, [rental, setPageTitle])

	// Pull the full property record so the wizard's "Объект аренды" step can
	// auto-fill with structured fields (type/area), not just rental snapshots.
	const { data: property } = useGetPropertyQuery(rental?.propertyId ?? '', {
		skip: !rental?.propertyId,
	})

	if (isLoading && !rental) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-sm text-(--color-muted-foreground)">{t('common.saving')}</p>
			</div>
		)
	}

	if (!rental) {
		return (
			<div className="flex h-full items-center justify-center">
				<p className="text-sm text-(--color-muted-foreground)">{t('rent.detail.notFound')}</p>
			</div>
		)
	}

	// propertyTitle already includes the address ("city, address") — don't join both.
	const title = (rental.propertyTitle ?? rental.propertyAddress) || `№${rental.id.slice(-7)}`

	// CreateDocumentWizard expects a Deal-shaped context.
	// Map rental fields to Deal so the wizard pre-fills dates and contact correctly.
	const wizardContext: Deal = {
		id:              rental.id,
		propertyId:      rental.propertyId,
		propertyTitle:   rental.propertyTitle,
		propertyAddress: rental.propertyAddress,
		contactId:       rental.tenantContactId,
		clientName:      rental.tenantName,
		clientPhone:     undefined,
		// Rental dates → wizard contract period (terms step)
		startDate:       rental.startDate,
		endDate:         rental.endDate,
		// Rental amount → wizard payment amount fallback
		amount:          rental.rentAmount,
		stage:           'NEW',
		status:          'ACTIVE',
		createdAt:       rental.createdAt,
		updatedAt:       rental.updatedAt,
	}

	function handleTemplatePicked(tpl: DocumentTemplate) {
		setChosenTemplate(tpl)
		setSelectTemplateOpen(false)
		setWizardOpen(true)
	}

	async function handleDocumentSaved(payload: {
		template: DocumentTemplate
		title:    string
		data?:    unknown
	}) {
		if (!rental) return
		const doc = await createDocument({
			templateId:     payload.template.id,
			type:           payload.template.type,
			title:          payload.title,
			entityType:     'RENTAL',
			entityId:       rental.id,
			filledDataJson: payload.data ? JSON.stringify(payload.data) : undefined,
		}).unwrap().catch((err) => {
			console.error('createDocument failed:', err)
		})
		if (doc?.id) {
			await updateDocumentStatus({ id: doc.id, body: { status: 'DATA_FILLED' } }).unwrap()
		}
		dispatch(rentalDocumentsApi.util.invalidateTags([{ type: 'RentalDocument', id: rental.id }]))
	}

	function handleUploadFromComputer() {
		setDocMenuOpen(false)
		fileInputRef.current?.click()
	}

	async function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0]
		e.target.value = ''
		if (!file || !rental) return
		try {
			setUploadError(null)
			const { uploadUrl, publicUrl } = await generateUploadUrl({
				contentType: file.type,
				fileName:    file.name,
			}).unwrap()

			await new Promise<void>((resolve, reject) => {
				const xhr = new XMLHttpRequest()
				xhr.open('PUT', uploadUrl)
				xhr.setRequestHeader('Content-Type', file.type)
				xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`HTTP ${xhr.status}`)))
				xhr.onerror = () => reject(new Error('Network error'))
				xhr.send(file)
			})

			const doc = await createDocument({
				type:       'OTHER',
				title:      file.name,
				entityType: 'RENTAL',
				entityId:   rental.id,
			}).unwrap()
			if (doc?.id) {
				await updateDocument({ id: doc.id, body: { fileUrl: publicUrl } }).unwrap()
			}
			dispatch(rentalDocumentsApi.util.invalidateTags([{ type: 'RentalDocument', id: rental.id }]))
		} catch (err) {
			// Was a bare console.error: the upload failed, the list stayed empty
			// and nothing on screen said why, so a rejected upload was
			// indistinguishable from never having clicked.
			//
			// The PUT rejects with a plain Error carrying the status, which
			// `extractErrorMessage` does not recognise — it reads RTK Query
			// shapes. Appending it is what turns "не удалось" into something
			// diagnosable without opening devtools.
			const detail = err instanceof Error ? err.message : undefined
			const message = extractErrorMessage(err) ?? t('rent.documents.uploadFailed')
			setUploadError(detail ? `${message} (${detail})` : message)
		}
	}

	return (
		<div className="relative flex h-full flex-col overflow-hidden">
			<div className="flex flex-col gap-3 border-b border-(--color-border) px-4 py-3">
				<div className="flex items-center gap-3">
					<h1
						className="flex-1 cursor-pointer text-2xl leading-8 tracking-[-0.6px] font-[500] text-(--color-foreground) underline-offset-4 hover:underline"
						onClick={() => void navigate({ to: '/properties/$propertyId', params: { propertyId: rental.propertyId } })}
					>
						{title}
					</h1>
					{rental.paymentStatus && (
						<Badge variant={PAYMENT_STATUS_VARIANTS[rental.paymentStatus]} size="md">
							{t(PAYMENT_STATUS_KEYS[rental.paymentStatus])}
						</Badge>
					)}
					<DropdownMenu>
						<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label={t('rent.detail.actions.menu')} />}>
							<EllipsisVerticalIcon className="size-4" />
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="min-w-44">
							{canEditRental && (
								<>
									<DropdownMenuItem onClick={() => setEditOpen(true)}>
										{t('rent.detail.actions.edit')}
									</DropdownMenuItem>
									<DropdownMenuSub>
										<DropdownMenuSubTrigger>
											{t('rent.detail.actions.changeStatus', { defaultValue: 'Сменить статус' })}
										</DropdownMenuSubTrigger>
										<DropdownMenuSubContent>
											{RENTAL_STATUSES.map((s) => (
												<DropdownMenuItem
													key={s}
													disabled={rental.status === s || isStatusUpdating}
													onClick={() => {
														void updateStatus({ id: rental.id, body: { status: s } }).catch(() => {})
													}}
												>
													{t(RENTAL_STATUS_KEYS[s])}
												</DropdownMenuItem>
											))}
										</DropdownMenuSubContent>
									</DropdownMenuSub>
									<DropdownMenuSeparator />
								</>
							)}
							<DropdownMenuItem onClick={() => void navigate({ to: '/rent/active' })}>
								{t('rent.detail.actions.backToList')}
							</DropdownMenuItem>
							{canDeleteRental && (
								<>
									<DropdownMenuSeparator />
									<DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
										{t('rent.detail.actions.delete', { defaultValue: 'Удалить' })}
									</DropdownMenuItem>
								</>
							)}
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<RentalSummaryBar rental={rental} />

				<div className="flex items-end">
				<Tabs value={tab} onValueChange={setTab} className="flex-1">
					<TabsList variant="line">
						{tabs.map(({ value, labelKey }) => (
							<TabsTrigger key={value} variant="line" value={value} className="group/tab gap-1.5">
								{t(labelKey)}
								{value === 'tasks' && canCreateTask && (
									<TabActionButton
										ariaLabel={t('tasks.create')}
										onClick={() => setTaskFormOpen(true)}
									>
										<PlusIcon className="size-3.5" />
									</TabActionButton>
								)}
								{value === 'invoices' && canCreateInvoice && (
									<TabActionButton
										ariaLabel={t('rent.invoices.actions.create')}
										onClick={() => setCreateInvoiceOpen(true)}
									>
										<PlusIcon className="size-3.5" />
									</TabActionButton>
								)}
								{value === 'terms' && canEditRental && (
									<TabActionButton
										ariaLabel={t('rent.detail.actions.edit')}
										onClick={() => setEditOpen(true)}
									>
										<PencilIcon className="size-3.5" />
									</TabActionButton>
								)}
								{value === 'automation' && automation && canSetUpAutomation && (
									<DropdownMenu open={automationMenuOpen} onOpenChange={setAutomationMenuOpen}>
										<DropdownMenuTrigger
											render={
												<TabActionButton ariaLabel={t('rent.automation.actions.menu', { defaultValue: 'Действия' })}>
													<EllipsisVerticalIcon className="size-3.5" />
												</TabActionButton>
											}
										/>
										<DropdownMenuContent align="start" className="w-auto min-w-44">
											<DropdownMenuItem onClick={() => { setAutomationMenuOpen(false); setAutomationSetupOpen(true) }}>
												<PencilIcon />
												{t('rent.automation.actions.edit', { defaultValue: 'Настроить' })}
											</DropdownMenuItem>
											<DropdownMenuItem onClick={() => { setAutomationMenuOpen(false); setDuplicateOpen(true) }}>
												<CopyIcon />
												{t('rent.automation.actions.duplicate', { defaultValue: 'Продублировать' })}
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								)}
								{value === 'documents' && canCreateDocument && (
									<DropdownMenu open={docMenuOpen} onOpenChange={setDocMenuOpen}>
										<DropdownMenuTrigger
											render={
												<TabActionButton ariaLabel={t('rent.documents.actions.menu')}>
													<PlusIcon className="size-3.5" />
												</TabActionButton>
											}
										/>
										<DropdownMenuContent align="start" className="w-auto min-w-48">
											<DropdownMenuItem onClick={() => { setDocMenuOpen(false); setSelectTemplateOpen(true) }}>
												<FileTextIcon />
												{t('rent.documents.actions.createFromTemplate', { defaultValue: 'Создать по шаблону' })}
											</DropdownMenuItem>
											<DropdownMenuItem onClick={handleUploadFromComputer}>
												<FileUpIcon />
												{t('rent.documents.actions.uploadFromComputer', { defaultValue: 'Загрузить с компьютера' })}
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								)}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
				{chatCollapsed && (
					<button
						type="button"
						onClick={() => setChatCollapsed(false)}
						className="mb-px flex shrink-0 items-center gap-1 rounded-(--radius-md) bg-(--color-secondary) px-2 py-1.5 text-sm font-[500] text-(--color-primary) transition-colors hover:bg-(--color-secondary-hover)"
						aria-label={t('rent.detail.chat.expand')}
					>
						<ChevronLeftIcon className="size-4" />
						<span className="px-1">{t('rent.detail.chat.label')}</span>
						{rental.messageCount != null && rental.messageCount > 0 && (
							<span className="inline-flex min-w-5 items-center justify-center rounded-full bg-(--color-destructive) px-1.5 text-xs leading-4 font-[600] text-(--color-primary-foreground)">
								{rental.messageCount}
							</span>
						)}
					</button>
				)}
				</div>
			</div>

			<div className={cn(
				'grid flex-1 gap-4 overflow-hidden px-4 py-4',
				chatCollapsed ? 'grid-cols-[1fr]' : 'grid-cols-[1fr_440px]'
			)}>
				<div className="overflow-auto">
					{tab === 'info' && <RentalInfoSection rental={rental} t={t} />}
					{tab === 'terms' && rental.propertyId && (
						<RentalTermsSection rental={rental} />
					)}
					{tab === 'invoices' && <RentalInvoicesSection rentalId={rental.id} />}
					{tab === 'documents' && (
						<div className="flex flex-col gap-3">
							{uploadError && (
								<Alert variant="destructive">
									<AlertDescription>{uploadError}</AlertDescription>
								</Alert>
							)}
							<RentalDocumentsSection rentalId={rental.id} />
						</div>
					)}
					{tab === 'tasks' && <RentalTasksSection rentalId={rental.id} onCreateTask={() => setTaskFormOpen(true)} />}
					{tab === 'notes' && <RentalNotesSection rentalId={rental.id} />}
					{tab === 'automation' && (
						<RentalAutomationSection
							rentalId={rental.id}
							onSetup={canSetUpAutomation ? () => setAutomationSetupOpen(true) : undefined}
						/>
					)}
					{tab === 'history' && <RentalHistorySection rentalId={rental.id} />}
				</div>

				{!chatCollapsed && (
					<div className="overflow-hidden">
						{/* The same component the deal and tenant pages use. The page
						    used to swap in the deal's chat whenever a rental had a dealId,
						    so the same tab behaved like two different screens and the
						    rental's own messages were unreachable from a converted rental.
						    This serves the merged thread and routes sending by channel. */}
						<ConversationChat
							rentalId={rental.id}
							dealId={rental.dealId}
							onCollapse={() => setChatCollapsed(true)}
						/>
					</div>
				)}
			</div>

			<EditRentalDialog rental={rental} open={editOpen} onOpenChange={setEditOpen} />

			<ConfirmDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				title={t('rent.detail.actions.deleteConfirm', { defaultValue: 'Удалить эту аренду?' })}
				confirmLabel={t('common.delete', { defaultValue: 'Удалить' })}
				cancelLabel={t('common.cancel', { defaultValue: 'Отмена' })}
				variant="destructive"
				loading={isDeleting}
				onConfirm={async () => {
					await deleteRental(rental.id).unwrap()
					void navigate({ to: '/rent/active' })
				}}
			/>

			<AutomationSetupDialog
				open={automationSetupOpen}
				onOpenChange={setAutomationSetupOpen}
				rentalId={rental.id}
				rental={rental}
				initial={automation ?? undefined}
			/>

			<DuplicateAutomationDialog
				open={duplicateOpen}
				onOpenChange={setDuplicateOpen}
				onRentalPicked={(rentalId, targetRental) => {
					setDuplicateOpen(false)
					setDuplicateTarget({ rentalId, rental: targetRental })
				}}
			/>

			{duplicateTarget && (
				<AutomationSetupDialog
					open={duplicateTarget !== null}
					onOpenChange={(v) => { if (!v) setDuplicateTarget(null) }}
					rentalId={duplicateTarget.rentalId}
					rental={duplicateTarget.rental}
					initial={automation ?? undefined}
				/>
			)}

			<CreateInvoiceDialog open={createInvoiceOpen} onOpenChange={setCreateInvoiceOpen} rental={rental} />

			<TaskFormDialog
				open={taskFormOpen}
				onOpenChange={setTaskFormOpen}
				initialBinding={{ kind: 'RENTAL', id: rental.id }}
				lockBinding
			/>

			<SelectTemplateDialog
				open={selectTemplateOpen}
				templates={templates}
				onOpenChange={setSelectTemplateOpen}
				onConfirm={handleTemplatePicked}
			/>

			<CreateDocumentWizard
				open={wizardOpen}
				template={chosenTemplate}
				deal={wizardContext}
				property={property ? {
					objectType:      formatObjectType(property, t),
					address:         property.address,
					cadastralNumber: property.cadastralNumber,
					area:            property.area,
					// Rental terms override property defaults — rental has the confirmed amounts
					rentAmount:      rental.rentAmount,
					rentCurrency:    rental.currency,
					depositAmount:   rental.depositAmount,
					penaltyRate:     rental.penaltyRate,
					// The VAT snapshot frozen on this rental, not the property's
					// current terms: the contract must print what was agreed.
					vatPercent:      rental.vatPercent,
					vatIncluded:     rental.vatIncluded,
					vatClause:       rental.vatClause,
				} : undefined}
				onOpenChange={(v) => {
					setWizardOpen(v)
					if (!v) setChosenTemplate(null)
				}}
				onSave={handleDocumentSaved}
			/>

			<input
				ref={fileInputRef}
				type="file"
				accept="application/pdf,.doc,.docx,image/jpeg,image/png,image/webp,image/heic,image/heif"
				className="hidden"
				onChange={handleFilePicked}
			/>
		</div>
	)
}
